import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography, Card, CardContent, IconButton, TextField } from '@mui/material';
import { FaEdit } from 'react-icons/fa';
import { skillsApi } from '../../../../../../../services/api';
import { useAlert } from '../../../../../../../contexts/AlertContext';
import Modal from '../../../../../../common/Modal';
import { COLORS } from '../../../../../../../constants/radar';
import EmptyState from '../../../../../../common/EmptyState';

const CompetenciesDisplay = ({ 
  competencies = [], 
  onUpdate = null,
  title = "Competencies",
  emptyStateTitle = "No Competencies Found",
  emptyStateDescription = "No competency levels have been defined yet.",
  readonly = false,
  onEditSubmit = null
}) => {
  const { showAlert } = useAlert();
  const [editModal, setEditModal] = useState({
    open: false,
    competency: null,
    description: ''
  });

  const handleEditClick = (competency) => {
    if (readonly) return;
    
    setEditModal({
      open: true,
      competency,
      description: competency.description || ''
    });
  };

  const handleEditClose = () => {
    setEditModal({
      open: false,
      competency: null,
      description: ''
    });
  };

  const handleEditSubmit = async () => {
    try {
      if (onEditSubmit) {
        // Use the custom API action if provided
        await onEditSubmit(editModal.competency, editModal.description);
      } else {
        // Fallback to the default API call
        await skillsApi.updateCompetencyDescription(editModal.competency.id, editModal.description);
      }
      
      showAlert('Competency description updated successfully', 'success');
      handleEditClose();
      
      // Call the onUpdate callback if provided
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      showAlert(error.message || 'Failed to update competency description', 'error', error);
    }
  };

  // Helper function to get competency level name
  const getCompetencyLevelName = (competency) => {
    return competency.competencyLevel?.name || 'Not Provided';
  };

  // Helper function to get competency level color
  const getCompetencyLevelColor = (competency) => {
    return competency.competencyLevel?.color || '#CCCCCC';
  };

  if (!competencies?.length) {
    return (
      <EmptyState
        title={emptyStateTitle}
        description={emptyStateDescription}
      />
    );
  }

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 3 }}>
        {[...competencies]
          .sort((a, b) => {
            // Sort by level if available, otherwise keep original order
            // Handle sorting with potential negative level values
            // Convert undefined/null levels to Number.MIN_SAFE_INTEGER to ensure they appear first
            const getLevelValue = (comp) => {
              return comp.competencyLevel?.level !== undefined && 
                     comp.competencyLevel?.level !== null ? 
                     comp.competencyLevel.level : 
                     Number.MIN_SAFE_INTEGER;
            };
            
            const levelA = getLevelValue(a);
            const levelB = getLevelValue(b);
            return levelA - levelB;
          })
          .map((competency) => (
          <Card key={competency.id} sx={{ 
            backgroundColor: 'white',
            borderRadius: '8px',
            border: `1px solid ${getCompetencyLevelColor(competency)}`,
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            opacity: 0.9,
            '&:hover': {
              opacity: 1,
              backgroundColor: `${getCompetencyLevelColor(competency)}20`
            }
          }}>
            <CardContent>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                mb: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: getCompetencyLevelColor(competency),
                    border: '1px solid transparent'
                  }} />
                  <Typography variant="h6" sx={{ 
                    color: COLORS.PRIMARY,
                    fontSize: '16px',
                    fontWeight: 600
                  }}>
                    {getCompetencyLevelName(competency)}
                  </Typography>
                </Box>
                {!readonly && (
                  <IconButton
                    size="small"
                    onClick={() => handleEditClick(competency)}
                    sx={{ 
                      color: COLORS.PRIMARY,
                      '&:hover': {
                        backgroundColor: `${COLORS.PRIMARY}10`
                      }
                    }}
                  >
                    <FaEdit />
                  </IconButton>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {competency.description || <em>No description provided</em>}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {!readonly && editModal.open && createPortal(
        <Modal
          isOpen={editModal.open}
          onClose={handleEditClose}
          title={`Edit ${editModal.competency ? getCompetencyLevelName(editModal.competency) : ''}`}
          onSubmit={handleEditSubmit}
          submitText="Save Changes"
          cancelText="Cancel"
          submitDisabled={!editModal.description.trim()}
        >
          <Box sx={{ p: 2 }}>
            <TextField
              label="Description"
              multiline
              rows={4}
              fullWidth
              value={editModal.description}
              onChange={(e) => setEditModal(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter competency level description"
              error={!editModal.description.trim()}
              helperText={!editModal.description.trim() ? "Description is required" : ""}
            />
          </Box>
        </Modal>,
        document.body
      )}
    </>
  );
};

export default CompetenciesDisplay; 